import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Workflow, GraduationCap, ClipboardList, CalendarClock, Flag, Building2, SlidersHorizontal, Database, Handshake, Mail, PenLine,
  Bell, Users, ShieldCheck, UserCheck, Gift, HelpCircle, LifeBuoy, UploadCloud, FileSearch, Palette, ScrollText, Trash2,
  BookOpen, Settings, CheckSquare, FileText, Sparkles, Library, Gauge, Target, CalendarRange, FolderOpen, Search, X,
  BookUser, UserPlus, ChevronDown, Clock, ArrowRight, UserX, Lock, Check, Eye, KeyRound,
} from "lucide-react";
import { SECTIONS, COMMON_TASKS, AREA_LABEL, ACCESS_LABEL, ROLE_MATRIX, useAdminCan } from "@/pages/admin/adminCards";

const ICONS = {
  Workflow, GraduationCap, ClipboardList, CalendarClock, Flag, Building2, SlidersHorizontal, Database, Handshake, Mail, PenLine,
  Bell, Users, ShieldCheck, UserCheck, Gift, HelpCircle, LifeBuoy, UploadCloud, FileSearch, Palette, ScrollText, Trash2,
  BookOpen, Settings, CheckSquare, FileText, Sparkles, Library, Gauge, Target, CalendarRange, FolderOpen, BookUser, UserPlus, UserX,
};
const RECENT = "tr-admin-recent";
const SHOW = "tr-admin-show";
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
const recent = () => { try { return JSON.parse(localStorage.getItem(RECENT)) || []; } catch { return []; } };
const remember = (label) => localStorage.setItem(RECENT, JSON.stringify([label, ...recent().filter((x) => x !== label)].slice(0, 4)));

function matches(card, q) {
  const hay = `${card.label} ${card.desc} ${(card.keywords || []).join(" ")} ${(card.links || []).map((l) => l.label).join(" ")}`.toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

export default function AdminCenter() {
  const { can, isAccountAdmin, isFullAdmin, groupNames } = useAdminCan();
  const [q, setQ] = useState("");
  const [closed, setClosed] = useState({});
  const [showAll, setShowAll] = useState(() => isFullAdmin && localStorage.getItem(SHOW) === "all");
  const [guide, setGuide] = useState(false);
  const all = isFullAdmin && showAll;
  const state = (c) => (can[c.scope] ? "manage" : c.view && can[c.view] ? "view" : "locked");
  const sections = useMemo(() => SECTIONS.map((s) => ({ ...s, cards: s.cards.filter((c) => (all || state(c) !== "locked") && (!q.trim() || matches(c, q)))
    .map((c) => ({ ...c, state: state(c), links: state(c) === "manage" ? (c.links || []).filter((l) => can[l.scope]) : [] })) })).filter((s) => s.cards.length), [q, all, JSON.stringify(can)]); // eslint-disable-line react-hooks/exhaustive-deps
  const flat = sections.flatMap((s) => s.cards).filter((c) => c.state !== "locked");
  const recents = recent().map((l) => flat.find((c) => c.label === l)).filter(Boolean);
  const tasks = COMMON_TASKS.filter((t) => can[t.scope]).slice(0, 6);
  const groups = groupNames(["group_admin", "recruiting_manager"]);
  const setShow = (v) => { setShowAll(v); localStorage.setItem(SHOW, v ? "all" : "mine"); };
  return (
    <div className="space-y-6" data-testid="admin-center">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold text-slate-900">Admin Center</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="admin-center-scope">
            {isAccountAdmin ? "Manage organization-wide settings and every Group in this account." : `Manage the settings you're authorized to control${groups.length ? ` for ${groups.join(", ")}` : " in your Groups"}.`}
          </p>
        </div>
        <div className="relative w-full md:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tools or tasks (e.g. terminate person)" aria-label="Search Admin Center"
            data-testid="admin-center-search" className="pl-9 pr-8 bg-white h-10" />
          {q && <button onClick={() => setQ("")} aria-label="Clear search" data-testid="admin-center-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-700"><X className="w-3.5 h-3.5" /></button>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        {isFullAdmin ? (
          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5 text-sm" data-testid="admin-show-toggle">
            <span className="px-2 text-xs text-slate-500">Show:</span>
            {[[false, "My Tools"], [true, "All Admin Tools"]].map(([v, l]) => (
              <button key={l} onClick={() => setShow(v)} data-testid={`admin-show-${v ? "all" : "mine"}`}
                className={`px-2.5 py-1 rounded-md transition-colors ${all === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>))}
          </div>
        ) : <span />}
        <button onClick={() => setGuide(true)} data-testid="admin-access-guide-btn" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#006090] hover:underline">
          <KeyRound className="w-3.5 h-3.5" />Admin Access Guide</button>
      </div>
      {!q && (tasks.length > 0 || recents.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-4">
          {tasks.length > 0 && <Shortcuts title="Common Admin Tasks" testId="admin-common-tasks" items={tasks.map((t) => ({ ...t, key: t.label }))} />}
          {recents.length > 0 && <Shortcuts title="Recently Used" testId="admin-recent" items={recents.map((c) => ({ label: c.label, to: c.to, icon: c.icon, key: c.label }))} clock />}
        </div>
      )}
      {sections.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="admin-center-no-results">
          <p className="font-medium text-slate-700">No tools match "{q}"</p>
          <p className="text-sm text-slate-500 mt-1">Try a different word, like "email", "calendar" or "directory".</p>
        </div>
      )}
      {sections.map((s) => <Section key={s.title} section={s} open={!!q || !closed[s.title]} toggle={() => setClosed((p) => ({ ...p, [s.title]: !p[s.title] }))} />)}
      <AccessGuide open={guide} onClose={() => setGuide(false)} />
    </div>
  );
}

function Shortcuts({ title, items, testId, clock }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2 flex items-center gap-1.5">{clock && <Clock className="w-3.5 h-3.5" />}{title}</p>
      <div className="flex flex-wrap gap-2">
        {items.map((t) => { const Icon = ICONS[t.icon] || Settings; return (
          <button key={t.key} onClick={() => { remember(t.label); navigate(t.to); }} data-testid={`${testId}-${slug(t.label)}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:border-[#006090] hover:text-[#006090] transition-colors">
            <Icon className="w-3.5 h-3.5" />{t.label}</button>); })}
      </div>
    </div>
  );
}

function Section({ section, open, toggle }) {
  return (
    <section data-testid={`admin-section-${slug(section.title)}`}>
      <button type="button" onClick={toggle} aria-expanded={open} data-testid={`admin-section-toggle-${slug(section.title)}`}
        className="w-full flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-3 pb-2 border-b border-slate-200">
        <span>{section.title}{section.platform && <span className="ml-2 normal-case tracking-normal rounded bg-violet-50 text-violet-700 px-1.5 py-0.5">Platform Super Admin only</span>}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{section.cards.map((c) => <Card key={c.label} c={c} />)}</div>}
    </section>
  );
}

const STATE = {
  manage: { label: "Can Manage", icon: Check, cls: "text-emerald-700" },
  view: { label: "View Only", icon: Eye, cls: "text-amber-700" },
  locked: { label: "Locked", icon: Lock, cls: "text-slate-400" },
};

function Card({ c }) {
  const navigate = useNavigate();
  const Icon = ICONS[c.icon] || Settings;
  const locked = c.state === "locked";
  const st = STATE[c.state];
  const go = (to) => (e) => { e.stopPropagation(); if (locked) return; remember(c.label); navigate(to); };
  const req = c.scope === "super" ? "Super Admin only" : `Requires ${ACCESS_LABEL[c.scope].replace("+", "")}`;
  return (
    <div role="button" tabIndex={locked ? -1 : 0} aria-disabled={locked} onClick={go(c.to)} onKeyDown={(e) => { if (e.key === "Enter") go(c.to)(e); }} data-testid={`admin-card-${slug(c.label)}`}
      className={`group h-full min-h-[84px] w-full text-left rounded-xl border p-4 transition-[border-color,box-shadow] ${locked ? "border-dashed border-slate-200 bg-slate-50 opacity-70 cursor-not-allowed" : "cursor-pointer border-slate-200 bg-white shadow-sm hover:border-[#006090]/60 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#006090]"}`}>
      <div className="flex items-start gap-3">
        <span className={`shrink-0 w-9 h-9 grid place-items-center rounded-lg ${locked ? "bg-slate-200 text-slate-400" : "bg-[#006090]/10 text-[#006090] group-hover:bg-[#006090] group-hover:text-white"} transition-colors`}>
          {locked ? <Lock className="w-4 h-4" /> : <Icon className="w-[18px] h-[18px]" />}</span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800 text-sm leading-5">{c.label}</p>
          <p className="text-xs text-slate-500 mt-0.5 leading-snug">{c.desc}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] font-medium">
            <span className="rounded bg-slate-100 text-slate-600 px-1.5 py-0.5" title="What this affects" data-testid={`admin-card-scope-${slug(c.label)}`}>{AREA_LABEL[c.area]}</span>
            <span className="rounded border border-slate-200 text-slate-500 px-1.5 py-0.5" title="Minimum access" data-testid={`admin-card-access-${slug(c.label)}`}>{ACCESS_LABEL[c.scope]}</span>
            <span className={`inline-flex items-center gap-0.5 ${st.cls}`} data-testid={`admin-card-state-${slug(c.label)}`}><st.icon className="w-3 h-3" />{locked ? req : st.label}</span>
          </div>
          {c.links?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
              {c.links.map((l) => <button key={l.to} type="button" onClick={go(l.to)} data-testid={`admin-link-${slug(l.label)}`}
                className="text-xs font-medium text-[#006090] hover:underline inline-flex items-center gap-0.5">{l.label}<ArrowRight className="w-3 h-3" /></button>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccessGuide({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="admin-access-guide">
        <DialogHeader><DialogTitle>Admin Access Guide</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Tools each role can manage per area, always limited to that person's own Groups (Account Admin: whole Organization; Super Admin: System).</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b"><th className="py-1.5 pr-2 font-medium">Area</th>{ROLE_MATRIX.map(([r]) => <th key={r} className="py-1.5 px-2 font-medium whitespace-nowrap">{r}</th>)}</tr></thead>
            <tbody>
              {SECTIONS.map((s) => (
                <tr key={s.title} className="border-b last:border-0">
                  <td className="py-1.5 pr-2 font-medium text-slate-700">{s.title}</td>
                  {ROLE_MATRIX.map(([r, keys]) => { const n = s.cards.filter((c) => keys.includes(c.scope) || (c.view && keys.includes(c.view))).length; return (
                    <td key={r} className={`py-1.5 px-2 ${n ? "text-slate-700" : "text-slate-300"}`} data-testid={`guide-${slug(s.title)}-${slug(r)}`}>{n ? `${n} of ${s.cards.length}` : "—"}</td>); })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500">Support Staff can view Terminated People and manage Knowledge Center content but cannot change Group settings. Server-side checks always apply.</p>
      </DialogContent>
    </Dialog>
  );
}
